import { formatFhirDate, getFhirDate } from '@utils/fhir';

import type { ConsentModel } from 'generated/types';
import { ConsentStatus } from 'generated/types';

import { FhirResource } from './fhir-resource';

type ConsentType = ConsentModel;

// https://terminology.hl7.org/6.1.0/CodeSystem-consentscope.html
export enum ConsentScopeCode {
  Adr = 'adr',
  Research = 'research',
  PatientPrivacy = 'patient-privacy',
  Treatment = 'treatment',
}

export const ConsentScopeMap = new Map([
  [ConsentScopeCode.Adr, 'Advanced Care Directive'],
  [ConsentScopeCode.Research, 'Research'],
  [ConsentScopeCode.PatientPrivacy, 'Privacy Consent'],
  [ConsentScopeCode.Treatment, 'Treatment'],
]);

export class ConsentRecord extends FhirResource {
  protected model: ConsentType;

  public constructor(model: ConsentType) {
    super(model);
    this.model = model;
  }

  public get category() {
    const category = this.model.category.at(0);
    const coding = category?.coding?.at(0);
    const text = category?.text;
    const code = coding?.code;
    return code ?? text?.toLowerCase();
  }

  public get consentText() {
    return this.model.scope.text;
  }

  public getConsentDate() {
    return getFhirDate(this.model.dateTime);
  }

  public getConsentDateStr() {
    return formatFhirDate(this.model.dateTime, { showTime: false });
  }

  public hasScope(code: string) {
    const result = this.model.scope.coding?.some((c) => c.code === code);
    return result;
  }

  public hasCategory(code: string) {
    const result = this.model.category.some((cat) =>
      cat.coding?.some((c) => c.code === code)
    );
    return result;
  }

  public isActive() {
    return this.model.status === ConsentStatus.active;
  }

  public toggleActive() {
    if (this.model.status === ConsentStatus.active) {
      this.model.status = ConsentStatus.inactive;
    } else {
      this.model.status = ConsentStatus.active;
    }
  }

  public getModelInput() {
    return FhirResource.getCleanInput({
      ...this.model,
      policyRule: {
        coding: [
          {
            system:
              'https://terminology.hl7.org/5.0.0/ValueSet-v3-ActConsentDirective.html',
            code: '_ActConsentDirective',
            display: 'ActConsentDirective',
          },
        ],
      },
    });
  }
}
